import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

export type IconContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  disabled: boolean;
  size?: string;
  title?: string;
};

interface ContainerIconStyleProps {
  disabled: boolean;
  size?: string;
}

const nonForwardedProps = new Set(['disabled', 'size']);

const StyledIconContainer = styled.div
  .withConfig({
    shouldForwardProp: (prop, defaultValidatorFn) =>
      defaultValidatorFn(prop) && !nonForwardedProps.has(prop),
  })<ContainerIconStyleProps>`
  color: ${(p) => p.theme.colors.onBackground}80;
  fill: ${(p) => p.theme.colors.onBackground}80;

  display: flex;
  justify-content: center;
  align-items: center;

  ${(p) =>
    p.size
      ? `
        width: ${p.size};
        height: ${p.size};
      `
      : 'width: 2rem; height: 2rem;'};

  cursor: pointer;
  transition: 0.2s;

  :hover {
    scale: 1.2;
  }

  :active {
    scale: 0.8;
  }

  ${(p) =>
    p.disabled &&
    `
      :hover { scale: 1; }
      :active { scale: 1; }
      opacity: 0.5;
      cursor: not-allowed;
    `}

  > * {
    min-height: 100%;
    min-width: 100%;
  }
`;

const Tooltip = styled.div`
  position: fixed;
  transform: translateX(-50%);
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.onPrimary ?? '#fff'};
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.1;
  max-width: calc(100vw - 16px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 2147483647;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.06s ease;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);

  &[data-visible='true'] {
    opacity: 1;
  }
`;

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') ref(value);
      else (ref as React.MutableRefObject<T | null>).current = value;
    });
  };
}

type AnchorRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
};

const TOOLTIP_OFFSET_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

export const IconContainer = React.forwardRef<HTMLDivElement, IconContainerProps>(
  (
    {
      title,
      disabled,
      size,
      children,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      ...rest
    },
    forwardedRef
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const combinedRef = useMemo(
      () => mergeRefs<HTMLDivElement | null>(innerRef, forwardedRef),
      [forwardedRef]
    );

    const [tooltipState, setTooltipState] = useState<{
      visible: boolean;
      top: number;
      left: number;
      anchor: AnchorRect | null;
    }>({ visible: false, top: 0, left: 0, anchor: null });

    const getAnchorRect = useCallback((): AnchorRect | null => {
      const el = innerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom,
      };
    }, []);

    const computeTooltipPosition = useCallback(
      (anchor: AnchorRect, tooltipWidth: number, tooltipHeight: number) => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const centerX = anchor.left + anchor.width / 2;
        const halfTooltip = tooltipWidth / 2;

        const minCenterX = VIEWPORT_MARGIN_PX + halfTooltip;
        const maxCenterX = viewportWidth - VIEWPORT_MARGIN_PX - halfTooltip;

        const left =
          minCenterX > maxCenterX
            ? viewportWidth / 2
            : Math.min(Math.max(centerX, minCenterX), maxCenterX);

        const belowTop = anchor.bottom + TOOLTIP_OFFSET_PX;
        const aboveTop = anchor.top - TOOLTIP_OFFSET_PX - tooltipHeight;

        const fitsBelow =
          belowTop + tooltipHeight + VIEWPORT_MARGIN_PX <= viewportHeight;
        const fitsAbove = aboveTop >= VIEWPORT_MARGIN_PX;

        const top = fitsBelow
          ? belowTop
          : fitsAbove
            ? aboveTop
            : Math.max(
              VIEWPORT_MARGIN_PX,
              Math.min(
                belowTop,
                viewportHeight - VIEWPORT_MARGIN_PX - tooltipHeight
              )
            );

        return { left, top };
      },
      []
    );

    const updateTooltipPosition = useCallback(() => {
      const anchor = getAnchorRect();
      if (!anchor) return;

      const tooltipEl = tooltipRef.current;
      if (tooltipEl) {
        const tooltipRect = tooltipEl.getBoundingClientRect();
        const { left, top } = computeTooltipPosition(
          anchor,
          tooltipRect.width,
          tooltipRect.height
        );
        setTooltipState((prev) => ({ ...prev, anchor, left, top }));
        return;
      }

      setTooltipState((prev) => ({
        ...prev,
        anchor,
        left: anchor.left + anchor.width / 2,
        top: anchor.bottom + TOOLTIP_OFFSET_PX,
      }));
    }, []);

    const showTooltip = useCallback(() => {
      if (!title || disabled) return;
      updateTooltipPosition();
      setTooltipState((prev) => ({ ...prev, visible: true }));
    }, [disabled, title, updateTooltipPosition]);

    const hideTooltip = useCallback(() => {
      setTooltipState((prev) => ({ ...prev, visible: false, anchor: null }));
    }, []);

    useLayoutEffect(() => {
      if (!tooltipState.visible) return;
      if (!tooltipState.anchor) return;
      const tooltipEl = tooltipRef.current;
      if (!tooltipEl) return;

      const tooltipRect = tooltipEl.getBoundingClientRect();
      const { left, top } = computeTooltipPosition(
        tooltipState.anchor,
        tooltipRect.width,
        tooltipRect.height
      );

      setTooltipState((prev) => {
        if (!prev.visible || !prev.anchor) return prev;
        const leftChanged = Math.abs(prev.left - left) > 0.5;
        const topChanged = Math.abs(prev.top - top) > 0.5;
        return leftChanged || topChanged ? { ...prev, left, top } : prev;
      });
    }, [computeTooltipPosition, tooltipState.anchor, tooltipState.visible]);

    useEffect(() => {
      if (!tooltipState.visible) return;

      const onUpdate = () => updateTooltipPosition();
      window.addEventListener('scroll', onUpdate, true);
      window.addEventListener('resize', onUpdate);
      return () => {
        window.removeEventListener('scroll', onUpdate, true);
        window.removeEventListener('resize', onUpdate);
      };
    }, [tooltipState.visible, updateTooltipPosition]);

    return (
      <>
        <StyledIconContainer
          {...rest}
          ref={combinedRef}
          disabled={disabled}
          size={size}
          aria-label={title || undefined}
          onMouseEnter={(e) => {
            showTooltip();
            onMouseEnter?.(e);
          }}
          onMouseLeave={(e) => {
            hideTooltip();
            onMouseLeave?.(e);
          }}
          onFocus={(e) => {
            showTooltip();
            onFocus?.(e);
          }}
          onBlur={(e) => {
            hideTooltip();
            onBlur?.(e);
          }}
        >
          {children}
        </StyledIconContainer>

        {title && tooltipState.visible
          ? createPortal(
            <Tooltip
              ref={tooltipRef}
              data-visible={tooltipState.visible ? 'true' : 'false'}
              style={{ top: tooltipState.top, left: tooltipState.left }}
            >
              {title}
            </Tooltip>,
            document.body
          )
          : null}
      </>
    );
  }
);

IconContainer.displayName = 'IconContainer';

export default IconContainer;
